import React from 'react';

const AuditSection = () => {
  return (
    <section className="py-12 bg-white relative overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute top-10 left-0 w-64 h-64 bg-blue-100 rounded-full opacity-20 blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-10 right-0 w-64 h-64 bg-teal-100 rounded-full opacity-20 blur-3xl pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Prüfungssicher: Was Auditoren sehen
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Vollständige Dokumentation und transparente Nachweisführung auf Knopfdruck
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-teal-700 to-blue-500 mx-auto rounded-full mt-6"></div>
        </div>

        {/* 50/50 Layout Container */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

          {/* LEFT: White Card Container with Audit Points */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-gray-200">
            <div className="grid grid-cols-1 gap-6">
            {/* Item 1 */}
            <div className="flex items-start gap-4 group">
              <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1 group-hover:bg-teal-200 transition-colors">
                <svg className="w-5 h-5 text-teal-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">
                  Transparenter Überblick
                </h4>
                <p className="text-gray-700 leading-relaxed">
                  Vollständige Prüfungen auf Knopfdruck
                </p>
              </div>
            </div>

            {/* Item 2 */}
            <div className="flex items-start gap-4 group">
              <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1 group-hover:bg-teal-200 transition-colors">
                <svg className="w-5 h-5 text-teal-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">
                  Manipulationssicher
                </h4>
                <p className="text-gray-700 leading-relaxed">
                  Unveränderbare Dokumentation mit Zeitstempel
                </p>
              </div>
            </div>

            {/* Item 3 */}
            <div className="flex items-start gap-4 group">
              <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1 group-hover:bg-teal-200 transition-colors">
                <svg className="w-5 h-5 text-teal-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                </svg>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">
                  Zentrale Verfügbarkeit
                </h4>
                <p className="text-gray-700 leading-relaxed">
                  Jederzeit und überall verfügbar
                </p>
              </div>
            </div>

            {/* Item 4 */}
            <div className="flex items-start gap-4 group">
              <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1 group-hover:bg-teal-200 transition-colors">
                <svg className="w-5 h-5 text-teal-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">
                  PDF/CSV-Export
                </h4>
                <p className="text-gray-700 leading-relaxed">
                  Ein Klick für Behörden und Auditoren
                </p>
              </div>
            </div>

            {/* Item 5 */}
            <div className="flex items-start gap-4 group">
              <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1 group-hover:bg-teal-200 transition-colors">
                <svg className="w-5 h-5 text-teal-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">
                  Proaktiver Nachweis
                </h4>
                <p className="text-gray-700 leading-relaxed">
                  Compliance auf Abruf, ohne Suchen
                </p>
              </div>
            </div>

            {/* Item 6 */}
            <div className="flex items-start gap-4 group">
              <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1 group-hover:bg-teal-200 transition-colors">
                <svg className="w-5 h-5 text-teal-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">
                  Lückenlose Historie
                </h4>
                <p className="text-gray-700 leading-relaxed">
                  Vollständiger Audit-Trail mit Zeitstempel
                </p>
              </div>
            </div>
          </div>
          </div>

          {/* RIGHT: Benefits Section - Ihre Vorteile mit IDA */}
          <div className="space-y-6">
            {/* Section Title */}
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-6">
                Ihre Vorteile mit IDA
              </h3>
            </div>

            {/* Benefit 1: Risikominimierung */}
            <div className="bg-white rounded-xl p-6 border-t-4 border-teal-700 shadow-sm hover:shadow-md transition-shadow">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Risikominimierung</h3>
              <p className="text-gray-600 leading-relaxed">
                Frühzeitige Warnungen bei kritischen Spielersituationen und rechtlichen Anforderungen
              </p>
            </div>

            {/* Benefit 2: Effizienzsteigerung */}
            <div className="bg-white rounded-xl p-6 border-t-4 border-blue-500 shadow-sm hover:shadow-md transition-shadow">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Effizienzsteigerung</h3>
              <p className="text-gray-600 leading-relaxed">
                Automatisierte Prozesse reduzieren manuellen Aufwand und beschleunigen Abläufe
              </p>
            </div>

            {/* Benefit 3: Rechtssicherheit */}
            <div className="bg-white rounded-xl p-6 border-t-4 border-gray-800 shadow-sm hover:shadow-md transition-shadow">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Rechtssicherheit</h3>
              <p className="text-gray-600 leading-relaxed">
                Vollständige Dokumentation schützt vor rechtlichen Konsequenzen
              </p>
            </div>
          </div>

        </div>

        {/* DSGVO Banner - Full Width Below */}
        <div className="mt-8 bg-blue-600 rounded-2xl p-8 shadow-lg">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h4 className="text-xl font-bold text-white mb-2">
                Sicher, effizient und rechtlich abgesichert
              </h4>
              <p className="text-base text-blue-100">
                Mit IDA auf der sicheren Seite
              </p>
            </div>
            <div className="flex-shrink-0">
              <div className="text-white text-right">
                <div className="text-base uppercase tracking-wider font-medium text-blue-200 mb-1">
                  GEPRÜFT
                </div>
                <div className="text-xl font-bold tracking-tight">
                  DSGVO-konform
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AuditSection;
